import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { outbox, type DeadLetter } from '@/lib/outbox';

/**
 * §28 / ADR-010 — what this app knows about its own offline outbox.
 *
 * The queue itself is not here and must never be re-implemented here: it lives
 * in `@/lib/offline` and is wired to the browser in `@/lib/outbox`. This slice
 * only *observes* it, because the invariant the outbox guarantees — delivered,
 * queued, or dead-lettered, never nowhere — is worth nothing if the third case
 * is invisible. A rejected order sitting on disk that nobody is told about is
 * indistinguishable, to the operator, from a successful one.
 */
interface SyncState {
  /** What the app *believes*, which is not the same as what is true. */
  online: boolean;
  pending: number;
  /**
   * Work the server refused. It is not "gone" — it is in the outbox dead
   * letter, and the banner is where a human sees it and decides.
   */
  deadLetters: DeadLetter[];
  syncing: boolean;
}

const initialState: SyncState = {
  online: true,
  pending: 0,
  deadLetters: [],
  syncing: false,
};

/** The queue and the dead letter are always read together, so they cannot disagree. */
const snapshot = async () => ({
  pending: (await outbox.all()).length,
  deadLetters: await outbox.dead(),
});

export const countPending = createAsyncThunk('sync/count', snapshot);

export const flushOutbox = createAsyncThunk('sync/flush', async () => {
  const result = await outbox.flush();
  return { sent: result.sent, pending: result.remaining, deadLetters: await outbox.dead() };
});

/** Put a rejected entry back in the queue and try it again straight away. */
export const retryFailed = createAsyncThunk('sync/retryFailed', async (operationId: string) => {
  await outbox.retryDead(operationId);
  await outbox.flush();
  return snapshot();
});

/** Throw a rejected entry away — only ever behind an explicit confirmation. */
export const discardFailed = createAsyncThunk('sync/discardFailed', async (operationId: string) => {
  await outbox.discardDead(operationId);
  return snapshot();
});

export const discardAllFailed = createAsyncThunk('sync/discardAllFailed', async () => {
  await outbox.clearDead();
  return snapshot();
});

const syncSlice = createSlice({
  name: 'sync',
  initialState,
  reducers: {
    setOnline(state, action: PayloadAction<boolean>) {
      state.online = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(flushOutbox.pending, (state) => {
        state.syncing = true;
      })
      .addCase(flushOutbox.fulfilled, (state, action) => {
        state.syncing = false;
        state.pending = action.payload.pending;
        state.deadLetters = action.payload.deadLetters;
        // Delivery is the only proof of connectivity there is; `navigator.onLine`
        // only ever claimed that a network interface exists.
        if (action.payload.sent > 0) state.online = true;
      })
      .addCase(flushOutbox.rejected, (state) => {
        state.syncing = false;
      })
      // Every other thunk answers with the same paired snapshot.
      .addMatcher(
        (action): action is PayloadAction<{ pending: number; deadLetters: DeadLetter[] }> =>
          [
            countPending.fulfilled.type,
            retryFailed.fulfilled.type,
            discardFailed.fulfilled.type,
            discardAllFailed.fulfilled.type,
          ].includes(action.type),
        (state, action) => {
          state.pending = action.payload.pending;
          state.deadLetters = action.payload.deadLetters;
        },
      );
  },
});

export const { setOnline } = syncSlice.actions;
export default syncSlice.reducer;
